import React from "react";
import { Form } from "react-bootstrap";
import { Cpu, Gauge } from "lucide-react";

/**
 * AiModelPicker — the model + thinking controls for every admin/superadmin AI
 * authoring screen (syllabus, blogs, CMS copy, SEO, quiz / coding / interview
 * question banks, WhatsApp flows).
 *
 * Drive it from `useAiModels`:
 *
 *   const ai = useAiModels("/cms/ai/models", show);
 *   <AiModelPicker {...ai} />
 *   …then spread `ai.payload` into the generate request.
 *
 * TWO CONTROLS, NOT ONE
 * ─────────────────────
 * The model is the obvious knob; `reasoning_effort` is the one that decides
 * whether the admin waits twenty seconds or four minutes. "GPT-5.1 is slow" is
 * almost always the effort, not the model — which is why Thinking is a visible
 * control worded as the trade it actually is, rather than a technical setting
 * buried in a tooltip. It appears only for a model that thinks; a dead dropdown
 * reads as a broken one.
 *
 * The whole block disappears when there is nothing to choose between (an
 * enterprise install pointed at its own gateway, or a one-row list). The
 * generation still works — the server applies its own default when no model is
 * named, which is what these screens did before the picker existed.
 */

/* Worded as the wait, not as the setting. */
const EFFORTS = [
  { value: "low", label: "Fast", desc: "Quickest. Good for a short, simple piece" },
  { value: "medium", label: "Balanced", desc: "Noticeably better structure for a moderate wait" },
  { value: "high", label: "Thorough", desc: "Deepest structure. Can take several minutes" },
];

const SPEED_HINT = { fast: "fast", medium: "medium", slow: "slow" };

/* Converted to Tailwind utilities in Phase 5 (2026-08-24); ai-model-picker.scss is
 * deleted (TOKEN_CONTRACT §6.1). The two fields are identical and appear twice, so
 * their class strings are named once here — a const, NOT `@apply`: no new class
 * name, no new selector, nothing for another stylesheet to override.
 *
 * The label's icon rule is an arbitrary variant because the <svg> is lucide's own
 * element, not markup this component writes attributes on.
 *
 * `--text-tertiary` was read as `var(--text-tertiary, #9ca3af)` in two places. The
 * token is declared, so the fallback was dead — and it was a light-mode grey that
 * would have been wrong in dark had it ever fired. */
const FIELD = "tw-flex tw-flex-col tw-gap-[3px] tw-min-w-0";
const LABEL =
  "tw-text-[0.74rem] tw-font-semibold tw-text-content-muted " +
  "[&_svg]:tw-mr-[3px] [&_svg]:tw-align-[-1px] [&_svg]:tw-text-content-subtle";
const HINT = "tw-text-[0.7rem] tw-leading-[1.45] tw-text-content-subtle";
/* `col-span-full` is inert on the !pickable branch, where this renders outside the
 * grid — exactly as `grid-column: 1 / -1` was. */
const ERROR = "tw-col-span-full tw-m-0 tw-text-[0.72rem] tw-text-warning-fg";

const AiModelPicker = ({
  models = [],
  model,
  setModel,
  reasoningEffort,
  setReasoningEffort,
  pickable = true,
  showEffort = false,
  activeModel,
  error = "",
  /** Inline hint under the model dropdown, when the surface has better wording. */
  hint,
  /** `false` drops the Thinking control even on a reasoning model — for a
   *  surface that has its own size dial and sets effort from it. */
  allowEffort = true,
  className = "",
}) => {
  if (!pickable) {
    // Nothing to choose between. The failed-to-load case still says so, because
    // an admin who was offered a picker yesterday should know why it is gone.
    return error ? <p className={ERROR}>{error}</p> : null;
  }

  return (
    <div
      className={`tw-grid tw-grid-cols-[repeat(auto-fit,minmax(190px,1fr))] tw-gap-3 ${className}`.trim()}
    >
      <label className={FIELD}>
        <span className={LABEL}><Cpu size={11} /> AI model</span>
        <Form.Select
          size="sm"
          value={model || ""}
          onChange={(e) => setModel(e.target.value)}
        >
          {models.map((m) => (
            <option key={m.id} value={m.id}>
              {m.label}{SPEED_HINT[m.speed] ? ` — ${SPEED_HINT[m.speed]}` : ""}
            </option>
          ))}
        </Form.Select>
        <span className={HINT}>
          {hint || activeModel?.note || "Longer, more structured pieces need a stronger model"}
        </span>
      </label>

      {allowEffort && showEffort && (
        <label className={FIELD}>
          <span className={LABEL}><Gauge size={11} /> Thinking</span>
          <Form.Select
            size="sm"
            value={reasoningEffort || ""}
            onChange={(e) => setReasoningEffort(e.target.value)}
          >
            {/* Empty = let the server decide. It scales effort with the size of
                the ask, which is the right answer until someone disagrees. */}
            <option value="">Recommended</option>
            {EFFORTS.map((x) => <option key={x.value} value={x.value}>{x.label}</option>)}
          </Form.Select>
          <span className={HINT}>
            {EFFORTS.find((x) => x.value === reasoningEffort)?.desc
              || "More thinking means a better result and a longer wait"}
          </span>
        </label>
      )}

      {error && <p className={ERROR}>{error}</p>}
    </div>
  );
};

export default AiModelPicker;
